import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, Database, RotateCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== SYSTEM TAB (cron + storage migration) ====================
function SystemTab({ language }) {
  const isLV = language === 'lv';
  const [migState, setMigState] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/uploads/migrate-blobs-to-storage/status`);
      setMigState(data);
    } catch (e) {
      console.error('mig status', e);
    }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 5000);
    return () => clearInterval(t);
  }, []);

  const triggerAnnualReset = async () => {
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/admin/compliance/annual-reset?force=true`);
      toast.success(
        isLV
          ? `${data.action === 'resumed' ? 'Atjaunoti' : 'Jau atjaunoti'} ${data.listings_resumed} sludinājumi (${data.year}. gads)`
          : `${data.action === 'resumed' ? 'Resumed' : 'Already done'} — ${data.listings_resumed} listings (${data.year})`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  const triggerMigration = async () => {
    setMigrating(true);
    try {
      const { data } = await axios.post(`${API}/admin/uploads/migrate-blobs-to-storage`);
      toast.success(data.message || (isLV ? 'Migrācija sākta!' : 'Migration started'));
      await fetchStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMigrating(false);
    }
  };

  const isMigRunning = migState?.running;
  const pending = migState?.pending_blobs ?? 0;
  const storageBacked = migState?.storage_backed_count ?? 0;

  return (
    <div className="space-y-6" data-testid="system-tab">
      {/* Annual compliance reset card */}
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <RotateCw className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-outfit text-lg font-semibold text-[#121212] mb-1">
              {isLV ? 'Gada compliance reset' : 'Annual compliance reset'}
            </h3>
            <p className="text-sm text-[#5C5C5C] mb-3">
              {isLV
                ? 'Atjauno visus sludinājumus, kas paslēpti par DAC7/AML limita pārsniegšanu pagājušajā gadā. Automātiski izpildās 1. janvārī, bet vari arī manuāli pārbaudīt.'
                : 'Resume all listings paused last year by DAC7/AML limit breaches. Runs automatically on Jan 1; manual trigger here for testing.'}
            </p>
            <Button
              onClick={triggerAnnualReset}
              disabled={resetting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="annual-reset-btn"
            >
              {resetting ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {isLV ? 'Atjauno...' : 'Resetting...'}</span>
              ) : (
                <span className="flex items-center gap-2"><RotateCw className="w-4 h-4" /> {isLV ? 'Veikt manuālo reset' : 'Run manual reset'}</span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Blob migration card */}
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#FFF8F0] flex items-center justify-center shrink-0">
            <Database className="w-6 h-6 text-[#D97736]" />
          </div>
          <div className="flex-1">
            <h3 className="font-outfit text-lg font-semibold text-[#121212] mb-1">
              {isLV ? 'Migrēt esošos attēlus uz object storage' : 'Migrate existing images to object storage'}
            </h3>
            <p className="text-sm text-[#5C5C5C] mb-4">
              {isLV
                ? 'Visi attēli, kas vēl ir saglabāti kā binārie blob\'i MongoDB, tiks pārvietoti uz Emergent object storage. Atbrīvo līdz 80% MongoDB izmēra.'
                : 'All images still stored as binary blobs in MongoDB will be moved to Emergent object storage. Frees up to 80% of MongoDB size.'}
            </p>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="border border-[#E5E4E0] rounded-xl p-3 bg-amber-50/40">
                <div className="text-xs text-[#5C5C5C] uppercase tracking-wide">
                  {isLV ? 'Vēl Mongo blob' : 'Still in Mongo'}
                </div>
                <div className="font-outfit text-2xl font-bold text-amber-700" data-testid="pending-blobs-count">
                  {pending}
                </div>
              </div>
              <div className="border border-[#E5E4E0] rounded-xl p-3 bg-emerald-50/40">
                <div className="text-xs text-[#5C5C5C] uppercase tracking-wide">
                  {isLV ? 'Object storage' : 'In storage'}
                </div>
                <div className="font-outfit text-2xl font-bold text-emerald-700">
                  {storageBacked}
                </div>
              </div>
            </div>

            {/* Progress when running */}
            {isMigRunning && migState && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm">
                <div className="flex items-center gap-2 text-blue-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>
                    {isLV ? 'Migrē...' : 'Migrating...'} {migState.migrated} / {migState.total}
                    {migState.failed > 0 && ` (${migState.failed} ${isLV ? 'neizdevās' : 'failed'})`}
                  </span>
                </div>
              </div>
            )}

            {migState?.finished_at && !isMigRunning && (
              <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800">
                {isLV ? 'Pēdējais skrējiens' : 'Last run'}: {migState.migrated} {isLV ? 'pārvietoti' : 'migrated'},{' '}
                {migState.failed} {isLV ? 'kļūdas' : 'errors'},{' '}
                {migState.skipped} {isLV ? 'izlaisti' : 'skipped'}
              </div>
            )}

            <Button
              onClick={triggerMigration}
              disabled={migrating || isMigRunning || pending === 0}
              className="bg-[#D97736] hover:bg-[#C06528] text-white"
              data-testid="migrate-blobs-btn"
            >
              {(migrating || isMigRunning) ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {isLV ? 'Notiek...' : 'Running...'}</span>
              ) : pending === 0 ? (
                <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> {isLV ? 'Nav ko migrēt' : 'Nothing to migrate'}</span>
              ) : (
                <span className="flex items-center gap-2"><Database className="w-4 h-4" /> {isLV ? `Migrēt ${pending} attēlus` : `Migrate ${pending} images`}</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SystemTab;
